/**
 * Dónde termina la navegación y empieza el contenido.
 *
 * POR QUÉ EXISTE ESTE ARCHIVO. `Toast.tsx` calculaba la posición de su host
 * sumando tokens a mano: `insets.top + SPACING.md + INTERACTION.minTouchTarget
 * + SPACING.sm`. Esa suma es correcta —y está MEDIDA— para un teléfono de
 * 390pt, que es donde se hizo la medición. En tablet es falsa por 64pt, y el
 * resultado no es cosmético: la barra roja del toast queda parada encima de la
 * flecha de atrás.
 *
 * Y los toasts de error no expiran nunca (`duration: 0`, decisión deliberada
 * para que un aviso crítico no se desvanezca antes de leerse). Las dos cosas
 * juntas son una trampa: te rebota la tarjeta, tocás atrás para cambiarla, y no
 * pasa nada. El único control vivo ahí arriba es la X del toast, y nada te dice
 * que tenés que tocarla. Tampoco es un artefacto del navegador de pruebas: en
 * una tablet real `ScreenContainer` también suma `insets.top`, así que el
 * término se cancela y el solape persiste.
 *
 * El defecto de fondo no era el número: era que DOS archivos describían la misma
 * geometría y solo uno se enteró cuando cambió. Acá vive una sola vez.
 *
 * SESGADO A FALLAR HACIA ABAJO, a propósito. El desplazamiento se calcula con el
 * chrome MÁS GRANDE que ese ancho puede introducir, no con el que la pantalla de
 * turno tenga. Una pantalla `full-bleed` en tablet no lleva tarjeta, así que ahí
 * el toast queda 64pt más abajo de lo estrictamente necesario. Eso es una
 * imperfección visual. Al revés —quedarse corto— es tapar la navegación. Entre
 * las dos, siempre la primera.
 */
import { SPACING } from '../brand'
import { INTERACTION } from './tokens'

/**
 * Alto de la barra superior de escritorio (`AppTopNav`).
 *
 * `AppShell` solo la monta cuando `isDesktop`, así que en móvil y tablet no
 * ocupa nada. Vive acá para que la barra y quien la esquiva no puedan
 * discrepar.
 */
export const APP_TOP_NAV_HEIGHT = 64

/**
 * Alto de la barra superior global de móvil/tablet (`MobileTopNav`), sin contar
 * `insets.top`: el control de 44pt más su respiro arriba y abajo. `AppShell` la
 * monta solo fuera de escritorio, y solo en raíces de tabs, así que en el resto
 * de rutas no ocupa nada.
 *
 * Vive acá por la misma razón que `APP_TOP_NAV_HEIGHT`: es lo que el host de
 * toasts tiene que esquivar. Con el número escrito en dos archivos, cambiar la
 * barra corre el toast encima de la navegación sin que nada avise.
 */
export const MOBILE_TOP_NAV_HEIGHT = INTERACTION.minTouchTarget + SPACING.sm * 2

/**
 * El "marco" que `ScreenContainer` le pone a la tarjeta en el rango de tablet:
 * `marginVertical` arriba más `padding` arriba. Es lo que empuja la fila del
 * encabezado hacia abajo y lo que el cálculo de 390pt no sabía.
 */
export const TABLET_CARD_CHROME = SPACING.xl * 2

/** Alto de la fila de navegación: el respiro de arriba más el control de 44pt. */
export const NAVIGATION_ROW_HEIGHT = SPACING.md + INTERACTION.minTouchTarget

export interface ChromeMetrics {
  /** `insets.top` del área segura. En escritorio web es 0. */
  insetTop: number
  isDesktop: boolean
  isTablet: boolean
  /**
   * La barra superior global de móvil/tablet está visible en esta ruta. En
   * escritorio es irrelevante (esa barra no se monta).
   */
  hasMobileTopNav: boolean
  /** `DESKTOP_SPACING.shellPaddingY[bp]`; 0 fuera de escritorio. */
  desktopShellPaddingY: number
}

/**
 * El borde inferior de la fila de navegación, en coordenadas de ventana.
 *
 * Debajo de este número no hay ningún control de navegación, en ningún ancho.
 * Es la única respuesta a "¿desde dónde puedo dibujar encima sin romper nada?".
 */
export function navigationRowBottom(m: ChromeMetrics): number {
  if (m.isDesktop) {
    // Escritorio: barra superior, el respiro del shell, y la fila del
    // encabezado. `insetTop` no entra porque en web es 0 y `ScreenContainer`
    // usa `desktopShellPaddingY` en su lugar, no el inset.
    return APP_TOP_NAV_HEIGHT + m.desktopShellPaddingY + NAVIGATION_ROW_HEIGHT
  }
  if (m.isTablet) {
    // Tablet: el área segura, el marco de la tarjeta, y la fila.
    return m.insetTop + TABLET_CARD_CHROME + NAVIGATION_ROW_HEIGHT
  }
  // Teléfono: barra superior global si la ruta la lleva; si no, solo el área
  // segura y la fila del encabezado. Este era el caso que ya estaba bien, y la
  // barra no cambia el insets: lo cambia por cuánto ocupa ella misma.
  if (m.hasMobileTopNav) {
    return m.insetTop + MOBILE_TOP_NAV_HEIGHT
  }
  return m.insetTop + NAVIGATION_ROW_HEIGHT
}
